import React, { useEffect } from 'react';
import styled, { css } from 'styled-components';
import Scene from '../scene/scene';
import { CallToAction } from '../../types/call-to-action';
import H5 from '../../modules/lower-ui-kit/components/typography/h5';
import { StyledWithChildrenProps } from '../../modules/lower-ui-kit/types/StyledWithChildrenProps';
import { Button } from '../../modules/lower-ui-kit';
import { ButtonType } from '../../modules/lower-ui-kit/components/button/button';
import H4 from '../../modules/lower-ui-kit/components/typography/h4';
import useSceneNavigator, { SceneRoutes } from '../../hooks/useSceneNavigator/useSceneNavigator';
import useDocumentTitle from '../../modules/lower-ui-kit/hooks/useDocumentTitle/useDocumentTitle';
import useNotifications from '../../hooks/useNotifications/useNotifications';

type ThankYouPageProps = {
  title: string;
  subtitle: string;
  cta: CallToAction;
  pageTitle: string;
};

function ThankYouSceneView({
  title,
  subtitle,
  cta,
  pageTitle,
}: ThankYouPageProps) {
  const { navigate } = useSceneNavigator();

  const titlePrefix = 'DocUpload | ';

  useDocumentTitle(`${titlePrefix}${pageTitle}`);

  const { clearNotifications } = useNotifications();

  const maintenanceInfo = process.env.MAINTENANCE_INFO ?? '';

  useEffect(() => {
    clearNotifications();
  }, [clearNotifications]);

  return (
    <Scene>
      <Container>
        <Title>
          {title}
        </Title>
        <Subtitle>
          {subtitle}
        </Subtitle>
        <MaintenanceInfo>
          {maintenanceInfo}
        </MaintenanceInfo>
        <UploadMoreButton
          onClick={() => navigate(SceneRoutes.LoginScene)}
          type={ButtonType.Form}
        >
          {cta.label}
        </UploadMoreButton>
      </Container>
    </Scene>
  );
}

export default ThankYouSceneView;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  margin-top: 275px;
  margin-top: 20vmin;
  padding: 0 30px;
  background: #ffffff;
`;

const Subtitle = styled(H4) < StyledWithChildrenProps > `
  margin-bottom: 36px;
  font-weight: bold;
  letter-spacing: -2px;
  line-height: 70px;
  font-size: 60px;
  color: #00023f;
  max-width: 730px;
  margin: 0 auto;
  margin-bottom: 35px;
  white-space: normal;
  ${({ theme }) => theme.media.tablet} {
    font-size: 52px;
    line-height: 56px;
    letter-spacing: -1.73px;
    max-width: 656px;
  }
`;

const Title = styled(H5) < StyledWithChildrenProps > `
  font-weight: bold;
  letter-spacing: -0.32px;
  line-height: 28px;
  font-size: 18px;
  color: #0600f5;
  margin-bottom: 14px;
  ${({ theme }) => css`
    color: ${theme.colors.primary};
  `}
`;

const MaintenanceInfo = styled(H5) < StyledWithChildrenProps >`
  ${({ theme }) => css`
    font-size: ${theme.maintenanceInfo.fontSize};
    color: ${theme.maintenanceInfo.color};
    margin: ${theme.maintenanceInfo.margin};
  `}
`;

const UploadMoreButton = styled(Button)`
animation: pop-in 0.5s forwards;
${({ theme }) => css`
    @keyframes pop-in {
      0% {
        opacity: 0;
        transform: ${theme.finishButton.transformFrom};
      }
      100% {
        opacity: 1; 
        transform: ${theme.finishButton.transformTo};
      }
    }
  `}
`;
